import { Injectable, inject } from '@angular/core';
import { TabService } from './tab.service';
import { ChatTab } from './types';

@Injectable({
  providedIn: 'root',
})
export class ScrollService {
  tabService = inject(TabService);

  // this tab is currently scrolling
  private activScrollTabId: string | undefined = undefined;

  // keep the last scroll-to element
  private lastScrollElementId: string | null | undefined = undefined;

  scrollToMessage(messageId: string | undefined, tab: ChatTab | undefined): void {
    if (!messageId || !tab) return;

    const scrollToElement = document.querySelector(`[data-message-id="${messageId}"]`);

    this.scrollToElement(scrollToElement, tab);
  }

  repeatLastScroll(tab: ChatTab | undefined): void {
    const lastScrollElement = document.querySelector(`[data-message-id="${this.lastScrollElementId}"]`);
    // next tick
    window.setTimeout(() => {
      this.scrollToElement(lastScrollElement, tab);
    }, 2);
  }

  scrollToElement(scrollToElement: Element | null, tab: ChatTab | undefined): void {
    if (!tab || !scrollToElement) return;

    // only scroll in active tab
    if (this.tabService.activeTab() !== tab) {
      return;
    }

    const scrollToElementId = this.getElementId(scrollToElement);

    if (this.lastScrollElementId !== scrollToElementId) {
      this.removeScrollListener();
      this.addScrollListener(tab, scrollToElement);
    }

    this.lastScrollElementId = scrollToElementId;

    const scrollContainer = this.findScrollContainer(scrollToElement);
    if (scrollContainer) {
      const offsetTop = (scrollToElement as HTMLElement).offsetTop;
      scrollContainer.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Helper functions
   */

  private getElementId(element: Element | null): string | null | undefined {
    let messageId = element?.getAttribute('data-message-id');
    if (element && !messageId) {
      messageId = element.getAttribute('id');
    }
    if (element && !messageId) {
      messageId = element.outerHTML.replace(element.innerHTML, '');
    }
    return messageId;
  }

  private onScrollEnd(): void {
    this.activScrollTabId = undefined;
  }

  private addScrollListener(tab: ChatTab | undefined, scrollToElement: Element) {
    this.activScrollTabId = tab?._id;
    const scrollContainer = this.findScrollContainer(scrollToElement);
    scrollContainer?.addEventListener('scrollend', this.onScrollEnd.bind(this));
  }

  private removeScrollListener() {
    const lastScrollElement = document.querySelector(`[data-message-id="${this.lastScrollElementId}"]`);
    const parentElement = this.findScrollContainer(lastScrollElement);
    parentElement?.removeEventListener('scrollend', this.onScrollEnd.bind(this));
  }

  private findScrollContainer(element: Element | null) {
    let parentElement = element?.parentElement;
    const overflowYRegex = /^overflow-y.*/;
    while (
      parentElement &&
      !Array.from(parentElement.classList).some((className) => overflowYRegex.test(className)) &&
      parentElement.tagName.toLowerCase() !== 'body'
    ) {
      parentElement = parentElement.parentElement;
    }
    return parentElement;
  }
}
